import React, { FC, useEffect, useRef, useState } from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { each, find, forEach, orderBy, toArray } from 'lodash';
import map from 'lodash/map';
import { useSelector } from 'react-redux';

import {
  CustomTooltip,
  getIcon,
  renderStatusTag,
  renderTimeStackedCell,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { DashboardWidgetTooltipIcon } from 'containers/Dashboard/utils/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { selectSharedState } from 'containers/SharedState/selectors';
import { toTitleCase } from 'utils/string';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ActiveWorkflows: FC<Props> = props => {
  const { isOpen, onClose } = props;
  const { blueprint, attachedWorkflows } = useSelector(selectBlueprints);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const { workflows } = useSelector(selectSharedState);

  const ref = useRef(document.querySelector('.portal-container'));
  const { environmentMapping } = useEnvironmentOptions();

  useEffect(() => {
    let items: Record<string, any>[] = [];
    each(attachedWorkflows.data, o => {
      const policy = find(o.policy, p => p.policy_id === blueprint.data.id);
      each(policy?.environment_ids, e => {
        if (!!environmentMapping?.[e])
          items.push({
            environmentId: e,
            environmentName: environmentMapping?.[e]?.name,
            ...o,
          });
      });
    });
    setData(orderBy(items, ['environmentName', 'name'], ['asc', 'asc']));
  }, [attachedWorkflows.data, environmentMapping, blueprint.data]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Environment</Box>,
      accessor: 'environmentName',
      render: ({ value }) => {
        return (
          <WithResourceIcon resourceType={IconTypes.Environment}>
            {value}
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Workflow',
      accessor: 'name',
      align: 'left',
      /*render: ({ value }) => {
        return renderClouds(toArray(value), 'clouds', accountMapping);
      },*/
    },
    {
      header: 'Targets',
      accessor: 'clients',
      align: 'left',
      render: ({ value }) => {
        let clients = new Set();
        forEach(value, o => clients.add(o?.client_name));
        return (
          <HStack spacing={1}>
            {map(toArray(clients), o => (
              <CustomTooltip label={toTitleCase(o as string)}>
                <Center boxSize={5}>{getIcon(o as string)}</Center>
              </CustomTooltip>
            ))}
          </HStack>
        );
      },
    },
    {
      header: 'Created',
      accessor: 'created_at',
      align: 'left',
      render: renderTimeStackedCell,
    },
    {
      header: 'Status',
      accessor: 'status',
      align: 'center',
      render: ({ value }) => (
        <Center>{renderStatusTag(value ? 'ACTIVE' : 'INACTIVE')}</Center>
      ),
    },
  ];

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <StackedHeader
            upper="Active Workflows"
            lower={blueprint.data?.name}
            type={IconTypes.Workflow}
          />
        }
        styles={{
          content: { maxW: '1400px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
        closeButton
        body={
          <Stack w="full" h="full" pt={3} spacing={5}>
            <HStack>
              <DashboardWidgetTooltipIcon />
              <Box color="primary">
                Showing only environments attached to this blueprint. Workflows
                can be reused across multiple environments.
              </Box>
            </HStack>
            <Box h="full">
              <Table
                data={data}
                columns={columns}
                cursor="pointer"
                isLoading={workflows.isLoading || attachedWorkflows.isLoading}
                /*onRowClick={row => {
                  dispatch(
                    actions.setAccount({
                      type: EnvironmentTypes.Environment,
                      environmentId: row?.id,
                      environment: row,
                    }),
                  );
                  navigate('/compliance');
                }}*/
              />
            </Box>
          </Stack>
        }
      />
    </>
  );
};
